<template>
  <ComponentHeader title="Search & Manage" subtitle="Search for opportunities or tasks, batch action tasks, and bulk delete duplicates" />

  <div class="task-admin-unified p-4">

    <!-- Search Mode Toggle -->
    <div class="mb-4">
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <label class="block text-sm font-medium text-gray-700 mb-2">Search Mode:</label>
        <div class="flex space-x-4">
          <label class="flex items-center cursor-pointer">
            <input
              type="radio"
              v-model="searchMode"
              value="opportunities"
              class="mr-2"
              @change="onSearchModeChange"
            />
            <span class="text-sm font-medium">Opportunities</span>
          </label>
          <label class="flex items-center cursor-pointer">
            <input
              type="radio"
              v-model="searchMode"
              value="tasks"
              class="mr-2"
              @change="onSearchModeChange"
            />
            <span class="text-sm font-medium">Tasks</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Search Controls -->
    <div class="mb-4 space-y-4">
      <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div class="space-y-4">
          <!-- Opportunity Search Filters -->
          <div v-if="searchMode === 'opportunities'" class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Opportunity ID:</label>
              <input
                v-model="opportunityFilters.opportunity_id"
                type="text"
                placeholder="Enter opportunity ID..."
                class="w-full p-2 border rounded-md"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Metric Name:</label>
              <select v-model="opportunityFilters.metric_name" class="w-full p-2 border rounded-md">
                <option value="">All Metrics</option>
                <option v-for="metric in availableFilters.opportunity.metric_name" :key="metric" :value="metric">
                  {{ metric }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
              <input
                v-model="opportunityFilters.patient_identifier"
                type="text"
                placeholder="Enter patient ID..."
                class="w-full p-2 border rounded-md"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Entities:</label>
              <EntitySearch
                :initial-selection="selectedEntityIds"
                @entities-selected="onEntitiesSelected"
              />
            </div>

            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Custom Metadata Search:</label>
              <div class="flex space-x-2">
                <input
                  v-model="opportunityFilters.metadata_key"
                  type="text"
                  placeholder="Metadata key..."
                  class="flex-1 p-2 border rounded-md"
                />
                <input
                  v-model="opportunityFilters.metadata_value"
                  type="text"
                  placeholder="Metadata value..."
                  class="flex-1 p-2 border rounded-md"
                />
              </div>
            </div>
          </div>

          <!-- Task Search Filters -->
          <div v-else class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Metric Name (Optional):</label>
              <select v-model="taskFilters.metric_name" @change="onMetricChange" class="w-full p-2 border rounded-md">
                <option value="">All Metrics</option>
                <option v-for="metric in availableFilters.opportunity.metric_name" :key="metric" :value="metric">
                  {{ metric }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Task Type:</label>
              <select v-model="taskFilters.task_type" class="w-full p-2 border rounded-md">
                <option value="">All Task Types</option>
                <option
                  v-for="taskType in filteredTaskTypes"
                  :key="taskType.id"
                  :value="taskType.id"
                >
                  {{ taskType.name }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Task Status:</label>
              <select v-model="taskFilters.status" class="w-full p-2 border rounded-md">
                <option value="">All Statuses</option>
                <option
                  v-for="status in availableFilters.task.status"
                  :key="status.id"
                  :value="status.id"
                >
                  {{ status.name }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Entities:</label>
              <EntitySearch
                :initial-selection="selectedEntityIds"
                @entities-selected="onEntitiesSelected"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
              <input
                v-model="taskFilters.patient_identifier"
                type="text"
                placeholder="Enter patient ID..."
                class="w-full p-2 border rounded-md"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Task ID:</label>
              <input
                v-model="taskFilters.task_id"
                type="text"
                placeholder="Enter task ID..."
                class="w-full p-2 border rounded-md"
              />
            </div>
          </div>

          <div class="flex gap-1">
            <button
              @click="applySearchFilters"
              class="btn-primary"
            >
              <font-awesome-icon :icon="['fas', 'search']" />
              Search
            </button>
            <button
              @click="clearSearchFilters"
              class="btn-secondary"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
              Clear
            </button>
          </div>
        </div>
      </div>

      <!-- Action Buttons and Stats -->
      <div class="flex justify-between items-center">
        <div class="flex items-center space-x-2">
          <!-- Opportunity Mode Actions -->
          <button
            v-if="searchMode === 'opportunities' && selectedRows.length > 0"
            @click="openBulkDeleteModal"
            class="btn-danger"
          >
            <i class="fas fa-trash mr-2"></i>
            Delete Selected ({{ selectedRows.length }})
          </button>

          <!-- Task Mode Actions -->
          <button
            v-if="searchMode === 'tasks' && selectedRows.length > 0"
            @click="showBatchActionModal = true"
            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
          >
            <i class="fas fa-tasks mr-2"></i>
            Batch Action ({{ selectedRows.length }})
          </button>

          <button
            @click="loadData"
            :disabled="!searchTriggered"
            class="btn-secondary"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
            Refresh
          </button>
        </div>

        <div class="flex space-x-3">
          <div class="bg-blue-50 rounded-lg px-3 py-2">
            <div class="text-lg font-bold text-blue-600">{{ rowData.length }}</div>
            <div class="text-xs text-gray-600">Search Results</div>
          </div>
        </div>
      </div>

      <!-- Legend (only for opportunities) -->
      <div v-if="rowData.length > 0 && searchMode === 'opportunities'" class="bg-gray-50 rounded-lg p-3">
        <div class="text-sm font-medium text-gray-700 mb-2">Legend:</div>
        <div class="flex space-x-4 text-xs">
          <div class="flex items-center">
            <div class="w-4 h-4 bg-green-100 border border-green-300 rounded mr-1"></div>
            <span>Primary (Keep)</span>
          </div>
          <div class="flex items-center">
            <div class="w-4 h-4 bg-red-100 border border-red-300 rounded mr-1"></div>
            <span>Deletable (No Actions)</span>
          </div>
          <div class="flex items-center">
            <div class="w-4 h-4 bg-orange-100 border border-orange-300 rounded mr-1"></div>
            <span>Deletable (Older Actions)</span>
          </div>
          <div class="flex items-center">
            <div class="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded mr-1"></div>
            <span>Has Recent Actions (Keep)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- AG Grid Table -->
    <div v-if="searchTriggered" class="h-full header-wrap-table">
      <AgGridVue
        :key="tableKey"
        :theme="standardListTheme"
        style="height: 900px"
        :column-defs="currentColumnDefs"
        :default-col-def="defaultColDef"
        :row-data="rowData"
        :group-display-type="groupDisplayType"
        :grid-options="currentGridOptions"
        :detail-row-auto-height="true"
        :loading="loading"
        :row-selection="rowSelection"
        :is-row-selectable="isRowSelectable"
        :get-row-id="getRowIdFunction"
        :row-class-rules="rowClassRules"
        @grid-ready="onGridReady"
        @selection-changed="onSelectionChanged"
      />
    </div>

    <!-- Bulk Delete Modal (Opportunities) -->
    <div v-if="showBulkDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4 text-red-600">
          <i class="fas fa-exclamation-triangle mr-2"></i>Confirm Bulk Delete
        </h3>

        <p class="text-gray-700 mb-4 whitespace-pre-line">{{ bulkDeleteMessage }}</p>

        <!-- Error Message -->
        <div v-if="bulkDeleteError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p class="text-red-700 text-sm">
            <i class="fas fa-exclamation-circle mr-2"></i>{{ bulkDeleteError }}
          </p>
        </div>

        <!-- Loading Indicator -->
        <div v-if="bulkDeleteLoading" class="mb-4 text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p class="text-gray-600 mt-2">Deleting opportunities...</p>
        </div>

        <div class="flex justify-end space-x-2">
          <button
            @click="showBulkDeleteModal = false; bulkDeleteError = null"
            :disabled="bulkDeleteLoading"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            @click="performBulkDelete"
            :disabled="bulkDeleteLoading"
            class="btn-danger"
          >
            <span v-if="!bulkDeleteLoading">
              <i class="fas fa-trash mr-2"></i>Delete
            </span>
            <span v-else>
              <i class="fas fa-spinner fa-spin mr-2"></i>Deleting...
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Batch Action Modal (Tasks) -->
    <div v-if="showBatchActionModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">Batch Action for {{ selectedRows.length }} Tasks</h3>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Select Action Type:</label>
          <select v-model="batchActionType" class="w-full p-2 border rounded-md">
            <option value="">Select an action...</option>
            <option
              v-for="actionType in availableActionTypes"
              :key="actionType.id"
              :value="actionType.id"
            >
              {{ actionType.name }}
            </option>
          </select>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Additional Notes (Optional):</label>
          <textarea
            v-model="batchActionNotes"
            class="w-full p-2 border rounded-md"
            rows="3"
            placeholder="Enter any additional notes..."
          ></textarea>
        </div>

        <div class="flex justify-end space-x-2">
          <button
            @click="showBatchActionModal = false"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            @click="performBatchAction"
            :disabled="!batchActionType || batchSubmitting"
            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            <FontAwesomeIcon v-if="batchSubmitting" :icon="['fas', 'spinner']" class="animate-spin mr-1" />
            {{ batchSubmitting ? 'Submitting...' : 'Submit Actions' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Individual Delete/Refresh Modals -->
    <ConfirmationModal
      :is-visible="isDeleteTaskModalOpen"
      :message="'Are you sure you want to delete this task?'"
      @confirm="deleteTask"
      @cancel="isDeleteTaskModalOpen = !isDeleteTaskModalOpen"
    />

    <ConfirmationModal
      :is-visible="isRefreshOpportunityModalOpen"
      :message="'Are you sure you want to refresh this opportunity?'"
      @confirm="refreshOpportunity"
      @cancel="isRefreshOpportunityModalOpen = !isRefreshOpportunityModalOpen"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import { standardListTheme } from '@/utils/common';
import CC_Button_Generic_Function from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Button_Generic_Function.vue';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import CC_Object_JSON_Modal from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Object_JSON_Modal.vue';
import EntitySearch from '@/components/widgetcomponents/Search_Widgets/EntitySearch_V2.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import { submitAction } from '@/services/api/tasks';

export default {
  name: 'TaskAdminUnified',
  components: {
    AgGridVue,
    ConfirmationModal,
    EntitySearch,
    ComponentHeader
  },
  data() {
    return {
      // Search mode
      searchMode: 'opportunities', // 'opportunities' or 'tasks'
      searchTriggered: false,

      // Data
      rowData: [],
      availableFilters: {
        opportunity: {
          metric_name: [],
          metrics: [] // Store full metric objects with IDs
        },
        task: {
          status: [],
          task_types: [],
          metric_task_types: [] // Task types filtered by selected metric
        }
      },

      // Opportunity filters
      opportunityFilters: {
        opportunity_id: '',
        metric_name: '',
        patient_identifier: '',
        metadata_key: '',
        metadata_value: ''
      },
      selectedEntityIds: [],

      // Task filters
      taskFilters: {
        task_id: '',
        task_type: '',
        metric_name: '',
        status: '',
        patient_identifier: ''
      },

      // Selection and modals
      selectedRows: [],
      showBulkDeleteModal: false,
      bulkDeleteLoading: false,
      bulkDeleteError: null,
      showBatchActionModal: false,
      batchActionType: '',
      batchActionNotes: '',
      batchSubmitting: false,
      availableActionTypes: [],
      isDeleteTaskModalOpen: false,
      delete_task_id: null,
      isRefreshOpportunityModalOpen: false,
      refreshOpportunityId: null,

      // Grid configuration
      loading: false,
      tableKey: 0,
      gridApi: null,
      groupDisplayType: 'singleColumn',
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        enableClickSelection: false,
        headerCheckbox: true,
      },
      defaultColDef: {
        filter: true,
        sortable: true,
        resizable: true,
        wrapText: true,
        autoHeight: true,
        cellClass: 'cell-theme-left-aligned',
      },
    };
  },
  computed: {
    standardListTheme() {
      return standardListTheme;
    },

    filteredTaskTypes() {
      // If a metric is selected and we have metric-specific task types, use those
      if (this.taskFilters.metric_name && this.availableFilters.task.metric_task_types.length > 0) {
        return this.availableFilters.task.metric_task_types;
      }

      // Otherwise show all task types
      return this.availableFilters.task.task_types;
    },

    getRowIdFunction() {
      if (this.searchMode === 'opportunities') {
        return (params) => String(params.data.opportunity_id);
      } else {
        return (params) => String(params.data.task_id || params.data.id);
      }
    },

    rowClassRules() {
      if (this.searchMode === 'opportunities') {
        return {
          'bg-red-50': params => params.data && params.data.duplicate_status === 'duplicate_deletable' && params.data.total_action_count === 0,
          'bg-orange-50': params => params.data && params.data.duplicate_status === 'duplicate_deletable' && params.data.total_action_count > 0,
          'bg-yellow-50': params => params.data && params.data.duplicate_status === 'duplicate_has_actions',
          'bg-green-50': params => params.data && params.data.duplicate_status === 'primary',
        };
      }
      return {};
    },

    bulkDeleteMessage() {
      const deletableCount = this.selectedRows.filter(
        opp => opp.duplicate_status === 'duplicate_deletable'
      ).length;
      const deletableWithActions = this.selectedRows.filter(
        opp => opp.duplicate_status === 'duplicate_deletable' && opp.total_action_count > 0
      ).length;
      const deletableNoActions = deletableCount - deletableWithActions;

      let message = `You have selected ${this.selectedRows.length} opportunities for deletion:\n\n`;
      if (deletableNoActions > 0) {
        message += `• ${deletableNoActions} have no actions\n`;
      }
      if (deletableWithActions > 0) {
        message += `• ${deletableWithActions} have actions but older than other duplicates\n`;
      }
      message += `\nAre you sure you want to delete these ${deletableCount} opportunities?`;
      return message;
    },

    currentColumnDefs() {
      return this.searchMode === 'opportunities' ? this.opportunityColumnDefs : this.taskColumnDefs;
    },

    currentGridOptions() {
      return this.searchMode === 'opportunities' ? this.opportunityGridOptions : this.taskGridOptions;
    },

    opportunityColumnDefs() {
      return [
        { field: 'opportunity_id', headerName: 'ID', cellRenderer: 'agGroupCellRenderer', width: 120 },
        {
          headerName: 'Status',
          field: 'duplicate_status',
          width: 180,
          cellRenderer: params => {
            if (!params.data) return '';
            const status = params.value;
            const hasActions = params.data.total_action_count > 0;
            let displayText = status;
            let className = '';

            if (status === 'duplicate_deletable' && hasActions) {
              displayText = 'Deletable (Older Actions)';
              className = 'text-orange-600 font-medium';
            } else if (status === 'duplicate_deletable' && !hasActions) {
              displayText = 'Deletable (No Actions)';
              className = 'text-red-600 font-medium';
            } else if (status === 'primary') {
              displayText = 'Primary';
              className = 'text-green-600 font-medium';
            } else if (status === 'duplicate_has_actions') {
              displayText = 'Has Actions';
              className = 'text-yellow-600 font-medium';
            } else if (status === 'unique') {
              displayText = 'Unique';
              className = 'text-gray-600';
            }
            return `<span class="${className}">${displayText}</span>`;
          }
        },
        { headerName: 'Metric Name', field: 'metric_name', filter: 'agSetColumnFilter', maxWidth: 150 },
        { headerName: 'Practice Name', field: 'practice_name', filter: 'agSetColumnFilter', cellClass: 'sensitive' },
        { field: 'patient_identifier', headerName: 'Patient ID', maxWidth: 150, filter: 'agTextColumnFilter', cellClass: 'sensitive' },
        { headerName: 'Claim Date', field: 'opportunity_metadata.clm_thru_dt', filter: 'agDateColumnFilter', maxWidth: 150 },
        { headerName: 'Dup Count', field: 'duplicate_count', width: 100, cellClass: 'text-center' },
        { field: 'valid_from', filter: 'agDateColumnFilter' },
        { field: 'valid_thru', filter: 'agDateColumnFilter' },
        { headerName: 'Actions', field: 'total_action_count', width: 80, cellClass: 'text-center' },
        { headerName: 'Last Action', field: 'last_action_date', width: 150, filter: 'agDateColumnFilter' },
        { cellRenderer: CC_Object_JSON_Modal, field: 'opportunity_metadata', headerName: 'Metadata', width: 100 },
        {
          headerName: 'Refresh',
          cellClass: 'text-center',
          width: 80,
          cellRenderer: CC_Button_Generic_Function,
          cellRendererParams: {
            function_type: 'refresh_opportunity',
            openRefreshOpportunityModal: (opportunity_id) => {
              this.refreshOpportunityId = opportunity_id;
              this.isRefreshOpportunityModalOpen = true;
            }
          }
        }
      ];
    },

    taskColumnDefs() {
      return [
        { field: 'id', headerName: 'Task ID', cellRenderer: 'agGroupCellRenderer', width: 120 },
        { field: 'task_type_name', headerName: 'Task Type', width: 150 },
        { field: 'task_status_name', headerName: 'Status', width: 120 },
        { field: 'patient_identifier', headerName: 'Patient ID', width: 150,
          valueGetter: params => {
            const opps = params.data?.opportunities || [];
            return opps.length > 0 ? opps[0].patient_identifier : '';
          }
        },
        { field: 'metric_name', headerName: 'Metric', width: 150,
          valueGetter: params => {
            const opps = params.data?.opportunities || [];
            return opps.length > 0 ? opps[0].metric?.name : '';
          }
        },
        { field: 'valid_from', width: 120 },
        { field: 'valid_thru', width: 120 },
        { cellRenderer: CC_Object_JSON_Modal, field: 'metadata', headerName: 'Metadata', width: 100 },
        {
          headerName: 'Delete',
          cellClass: 'text-center',
          width: 80,
          cellRenderer: CC_Button_Generic_Function,
          cellRendererParams: {
            function_type: 'delete_v2',
            openDeleteConfirmationModal: (task_data) => {
              this.delete_task_id = task_data.id;
              this.isDeleteTaskModalOpen = true;
            }
          }
        }
      ];
    },

    opportunityGridOptions() {
      return {
        masterDetail: true,
        detailCellRendererParams: {
          detailGridOptions: {
            masterDetail: true,
            getRowId: (params) => params.data.task_id,
            detailCellRendererParams: {
              detailGridOptions: {
                getRowId: (params) => params.data.action_id,
                columnDefs: [
                  { headerName: 'Action Type', field: 'action_type_name' },
                  { headerName: 'Action User', field: 'action_user_name' },
                  { headerName: 'Action Date', field: 'action_created_at' }
                ]
              },
              getDetailRowData: (params) => {
                params.successCallback(params.data.actions || []);
              },
            },
            columnDefs: [
              { field: 'task_id', cellRenderer: 'agGroupCellRenderer' },
              { field: 'task_type_name' },
              { field: 'task_order', sort: 'asc', hide: true },
              { field: 'valid_from' },
              { field: 'valid_thru' },
              { field: 'status_name' },
              { cellRenderer: CC_Object_JSON_Modal, field: 'task_metadata', headerName: 'Task Metadata' },
              {
                headerName: 'Delete Task',
                cellClass: 'text-center',
                cellRenderer: CC_Button_Generic_Function,
                cellRendererParams: {
                  function_type: 'delete_v2',
                  openDeleteConfirmationModal: (task_data) => {
                    this.delete_task_id = task_data.task_id;
                    this.isDeleteTaskModalOpen = true;
                  }
                }
              }
            ]
          },
          getDetailRowData: (params) => {
            params.successCallback(params.data.tasks || []);
          }
        },
        suppressAggFuncInHeader: true,
        domLayout: null
      };
    },

    taskGridOptions() {
      return {
        masterDetail: true,
        detailCellRendererParams: {
          detailGridOptions: {
            getRowId: (params) => params.data.id,
            columnDefs: [
              { headerName: 'Opportunity ID', field: 'id' },
              { headerName: 'Patient ID', field: 'patient_identifier' },
              { headerName: 'Valid From', field: 'valid_from' },
              { headerName: 'Valid Thru', field: 'valid_thru' },
              { cellRenderer: CC_Object_JSON_Modal, field: 'metadata', headerName: 'Metadata' }
            ]
          },
          getDetailRowData: (params) => {
            params.successCallback(params.data.opportunities || []);
          }
        },
        suppressAggFuncInHeader: true,
        domLayout: null
      };
    }
  },
  methods: {
    async fetchAvailableFilters() {
      try {
        // Fetch opportunity filters
        const oppResponse = await axios.get('api/opportunities/filters/');
        if (oppResponse.data.success && oppResponse.data.filters) {
          this.availableFilters.opportunity.metric_name = oppResponse.data.filters.metric_name || [];
        }

        // Fetch all metrics to get their IDs
        const metricsResponse = await axios.get('api/metrics/');
        if (metricsResponse.data) {
          this.availableFilters.opportunity.metrics = metricsResponse.data;
        }

        // Fetch task filters (task types and statuses)
        const taskTypeResponse = await axios.get('api/tasktypes/');
        if (taskTypeResponse.data) {
          this.availableFilters.task.task_types = taskTypeResponse.data;
        }

        const taskStatusResponse = await axios.get('api/taskstatuses/');
        if (taskStatusResponse.data) {
          this.availableFilters.task.status = taskStatusResponse.data;
        }
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    },

    onSearchModeChange() {
      this.searchTriggered = false;
      this.rowData = [];
      this.selectedRows = [];
      this.tableKey++;
    },

    async onMetricChange() {
      // Reset task type when metric changes
      this.taskFilters.task_type = '';

      // Fetch task types for the selected metric
      if (this.taskFilters.metric_name) {
        await this.fetchTaskTypesForMetric();
      } else {
        // Clear metric-specific task types if no metric selected
        this.availableFilters.task.metric_task_types = [];
      }
    },

    async fetchTaskTypesForMetric() {
      try {
        // Find the metric ID from the metric name
        const metric = this.availableFilters.opportunity.metrics.find(
          m => m.name === this.taskFilters.metric_name
        );

        if (!metric) {
          console.warn('Metric not found:', this.taskFilters.metric_name);
          this.availableFilters.task.metric_task_types = [];
          return;
        }

        // Fetch metric task types
        const response = await axios.get(`api/metrictasktypes/?metric=${metric.id}`);

        if (response.data) {
          // Extract task type details from the metric task type objects
          this.availableFilters.task.metric_task_types = response.data.map(mtt => ({
            id: mtt.task_type,
            name: mtt.task_type_name,
            color: mtt.task_type_color,
            icon: mtt.task_type_icon,
            task_order: mtt.task_order
          })).sort((a, b) => a.task_order - b.task_order);
        }
      } catch (error) {
        console.error('Error fetching task types for metric:', error);
        this.availableFilters.task.metric_task_types = [];
      }
    },

    buildOpportunitySearchParams() {
      const filterModel = {};

      if (this.opportunityFilters.opportunity_id) {
        filterModel['id'] = {
          filterType: 'number',
          type: 'equals',
          filter: parseInt(this.opportunityFilters.opportunity_id)
        };
      }
      if (this.opportunityFilters.metric_name) {
        // Find metric ID from name
        const metric = this.availableFilters.opportunity.metrics.find(
          m => m.name === this.opportunityFilters.metric_name
        );
        if (metric) {
          filterModel['program'] = metric.id;
        }
      }
      if (this.opportunityFilters.patient_identifier) {
        filterModel['searchTerm'] = this.opportunityFilters.patient_identifier;
      }
      if (this.opportunityFilters.metadata_key && this.opportunityFilters.metadata_value) {
        filterModel[`metadata__${this.opportunityFilters.metadata_key}`] = this.opportunityFilters.metadata_value;
      }

      return { filterModel, entityIds: this.selectedEntityIds.length > 0 ? this.selectedEntityIds : null };
    },

    onEntitiesSelected(entityIds) {
      this.selectedEntityIds = entityIds;
    },

    buildTaskSearchParams() {
      const params = {};

      if (this.taskFilters.task_id) {
        params.task_id = parseInt(this.taskFilters.task_id);
      }
      if (this.taskFilters.task_type) {
        params.task_type = parseInt(this.taskFilters.task_type);
      }
      if (this.taskFilters.status) {
        params.status = parseInt(this.taskFilters.status);
      }
      if (this.taskFilters.patient_identifier) {
        params.patient_identifier = this.taskFilters.patient_identifier;
      }
      if (this.selectedEntityIds.length > 0) {
        params.entity_ids = this.selectedEntityIds;
      }

      return params;
    },

    async loadData() {
      if (!this.searchTriggered) return;

      this.loading = true;
      try {
        if (this.searchMode === 'opportunities') {
          await this.loadOpportunityData();
        } else {
          await this.loadTaskData();
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadOpportunityData() {
      const { filterModel, entityIds } = this.buildOpportunitySearchParams();

      const requestData = {
        start_row: 0,
        end_row: 10000, // Large batch for admin search
        sortModel: [{ colId: 'id', sort: 'desc' }],
        filterModel: filterModel,
        entityIds: entityIds // Pass entity IDs from practice filter
      };

      const response = await axios.post('api/opportunities/list/', requestData);

      // Transform the response to match expected format for the grid
      if (response.data?.rowData) {
        this.rowData = response.data.rowData.map(opp => ({
          opportunity_id: opp.id,
          metric_name: opp.metric?.name || '',
          practice_name: opp.entity_id || '',
          patient_identifier: opp.patient_identifier,
          opportunity_metadata: opp.metadata || {},
          valid_from: opp.valid_from,
          valid_thru: opp.valid_thru,
          duplicate_status: opp.duplicate_status || 'unique',
          duplicate_count: opp.duplicate_count || 1,
          total_action_count: opp.total_action_count || 0,
          last_action_date: opp.last_action_date || null,
          tasks: (opp.tasks || []).map(task => ({
            task_id: task.id,
            task_type_name: task.task_type?.name || '',
            task_order: task.task_type?.id || 0,
            valid_from: task.valid_from,
            valid_thru: task.valid_thru,
            status_name: task.status?.name || '',
            task_metadata: task.metadata || {},
            actions: [] // Work items not included in this response
          }))
        }));
      } else {
        this.rowData = [];
      }
    },

    async loadTaskData() {
      const searchParams = this.buildTaskSearchParams();

      // Build filter model for worklist endpoint
      const filterModel = {};

      if (searchParams.patient_identifier) {
        // Search in opportunity patient_identifier through the relationship
        filterModel['opportunity__patient_identifier'] = {
          filterType: 'text',
          type: 'contains',
          filter: searchParams.patient_identifier
        };
      }

      // Build request for worklist endpoint
      const requestData = {
        start_row: 0,
        end_row: 1000, // Batch size for admin search
        sortModel: [{ colId: 'id', sort: 'desc' }],
        filterModel: filterModel,
        taskTypes: searchParams.task_type ? [searchParams.task_type] : [],
        entityId: null // Admin search across all entities
      };

      // Add task_id filter if provided
      if (searchParams.task_id) {
        requestData.filterModel['id'] = {
          filterType: 'number',
          type: 'equals',
          filter: searchParams.task_id
        };
      }

      // Add status filter if provided
      if (searchParams.status) {
        requestData.filterModel['status__id'] = {
          filterType: 'set',
          values: [searchParams.status]
        };
      }

      // Add entity filter if provided
      if (searchParams.entity_ids) {
        requestData.entityIds = searchParams.entity_ids;
      }

      const response = await axios.post('api/tasks/worklist/', requestData);
      this.rowData = response.data?.rowData || [];
    },

    applySearchFilters() {
      this.searchTriggered = true;
      this.loadData();
    },

    clearSearchFilters() {
      if (this.searchMode === 'opportunities') {
        this.opportunityFilters = {
          opportunity_id: '',
          metric_name: '',
          patient_identifier: '',
          metadata_key: '',
          metadata_value: ''
        };
        this.selectedEntityIds = [];
      } else {
        this.taskFilters = {
          task_id: '',
          task_type: '',
          metric_name: '',
          status: '',
          patient_identifier: ''
        };
        this.selectedEntityIds = [];
      }
      this.searchTriggered = false;
      this.rowData = [];
      this.selectedRows = [];
      if (this.gridApi) {
        this.gridApi.deselectAll();
      }
    },

    isRowSelectable(params) {
      if (this.searchMode === 'opportunities') {
        return params.data && params.data.duplicate_status === 'duplicate_deletable';
      }
      // For tasks, all active tasks are selectable for batch actions
      return params.data && params.data.task_status_name !== 'Completed';
    },

    onSelectionChanged() {
      if (this.gridApi) {
        this.selectedRows = this.gridApi.getSelectedRows();

        // If tasks are selected, fetch available action types for the first task's type
        if (this.searchMode === 'tasks' && this.selectedRows.length > 0) {
          this.fetchActionTypesForTasks();
        }
      }
    },

    async fetchActionTypesForTasks() {
      if (this.selectedRows.length === 0) return;

      const taskTypeId = this.selectedRows[0].task_type;
      try {
        const response = await axios.get(`api/taskactiontypes/?task_type=${taskTypeId}`);
        this.availableActionTypes = response.data.filter(at => at.active);
      } catch (error) {
        console.error('Error fetching action types:', error);
      }
    },

    onGridReady(params) {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', this.onWindowResize);
    },

    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },

    async deleteTask() {
      try {
        await axios.delete(`api/tasks/${this.delete_task_id}/`);
        await this.loadData();
        this.isDeleteTaskModalOpen = false;
        this.delete_task_id = null;
      } catch (error) {
        console.error('Error deleting task', error);
      }
    },

    async refreshOpportunity() {
      try {
        await axios.post(`api/opportunities/${this.refreshOpportunityId}/reconcile/`);
        await this.loadData();
        this.isRefreshOpportunityModalOpen = false;
        this.refreshOpportunityId = null;
      } catch (error) {
        console.error('Error refreshing opportunity', error);
      }
    },

    openBulkDeleteModal() {
      // Reset state when opening modal
      this.bulkDeleteLoading = false;
      this.bulkDeleteError = null;
      this.showBulkDeleteModal = true;
    },

    async performBulkDelete() {
      const opportunityIds = this.selectedRows.map(opp => opp.opportunity_id);

      this.bulkDeleteLoading = true;
      this.bulkDeleteError = null;

      try {
        const response = await axios.post('api/opportunities/bulk-delete/', { opportunity_ids: opportunityIds });

        // Success - close modal and refresh data
        this.showBulkDeleteModal = false;
        this.selectedRows = [];
        await this.loadData();

        // Optional: Show success notification
        if (response.data.message) {
          console.log('Bulk delete success:', response.data.message);
        }
      } catch (error) {
        console.error('Error performing bulk delete:', error);

        // Set error message to display in modal
        this.bulkDeleteError = error.response?.data?.message ||
                               'Failed to delete opportunities. Please try again.';
      } finally {
        this.bulkDeleteLoading = false;
      }
    },

    async performBatchAction() {
      if (!this.batchActionType || this.batchSubmitting) return;

      this.batchSubmitting = true;
      const taskIds = this.selectedRows.map(task => task.id);
      const metadata = {
        additionalData: {
          notes: this.batchActionNotes || '',
          batch_action: true
        }
      };

      try {
        const promises = taskIds.map(taskId =>
          submitAction({
            task_id: taskId,
            task_action_type_id: this.batchActionType,
            metadata: metadata
          })
        );

        const results = await Promise.all(promises);
        const skipped = results.filter(r => r.already_completed).length;

        this.showBatchActionModal = false;
        this.batchActionType = '';
        this.batchActionNotes = '';
        this.selectedRows = [];
        this.loadData();

        if (skipped > 0) {
          alert(`${skipped} of ${taskIds.length} task(s) were already completed and skipped.`);
        }
      } catch (error) {
        console.error('Error performing batch action:', error);
        alert('Error performing batch action. Please check console for details.');
      } finally {
        this.batchSubmitting = false;
      }
    }
  },
  async mounted() {
    await this.fetchAvailableFilters();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }
};
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
